import {Button, Flag, type ButtonVariant, type FlagProps} from 'platform/components';
import {HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import type {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {PriceBasis} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import type {TestIdProps} from 'shared/web';

import {BasisPrice} from '../BasisPrice/BasisPrice';
import {SelectableCard, type SelectableCardProps} from '../SelectableCard/SelectableCard';

export type OptionCardProps = TestIdProps & {
  title: string;
  description?: ReactNode;
  price?: {net: string; gross: string; basis: PriceBasis};
  currency: string;
  priceLabel?: string;
  isSelected: boolean;
  isDisabled: boolean;
  isLocked: boolean;
  reason?: string;
  actions?: {
    title: string;
    onClick: VoidFunction;
    variant?: ButtonVariant;
    isDisabled?: boolean;
  }[];
  flags?: FlagProps[];
  leading?: ReactNode;
  /** Defaults to `checkbox`; a `radio` group needs a shared `name`. */
  control?: SelectableCardProps['control'];
  name?: string;
  onToggle: VoidFunction;
};

export function OptionCard(props: OptionCardProps) {
  const flags: FlagProps[] = [
    ...(props.flags ?? []),
    ...(props.isLocked
      ? [
          {
            label: i18n.t('entity.newVehicle.labels.addedAutomatically', {
              defaultValue: 'Added automatically',
            }),
            colorScheme: 'blue' as const,
            isSubtle: true,
          },
        ]
      : []),
  ];
  const hasFooter = Boolean(props.reason) || Boolean(props.actions?.length);

  return (
    <SelectableCard
      control={props.control ?? 'checkbox'}
      name={props.name}
      title={props.title}
      isSelected={props.isSelected}
      isDisabled={props.isDisabled}
      isLocked={props.isLocked}
      reason={props.reason}
      leading={props.leading}
      onSelect={props.onToggle}
      data-testid={props['data-testid']}
      heading={
        <HStack spacing={2} align="center" wrap>
          <Text alternative color={props.isDisabled ? 'secondary' : 'primary'}>
            {props.title}
          </Text>
          {flags.map((flag) => (
            <Flag key={flag.label} size="small" {...flag} />
          ))}
        </HStack>
      }
      description={props.description}
      trailing={
        <HStack spacing={1} align="baseline" justify="flex-end">
          <Show when={props.price}>
            {(price) => (
              <BasisPrice
                {...price}
                prefix="+"
                currency={props.currency}
                size="small"
                alternative
                color={props.isDisabled ? 'secondary' : 'primary'}
                align="right"
                noWrap
              />
            )}
          </Show>
          <Show when={isNotNilOrEmpty(props.priceLabel) ? props.priceLabel : null}>
            {(priceLabel) => (
              <Text size="xSmall" color="secondary" noWrap>
                {priceLabel}
              </Text>
            )}
          </Show>
        </HStack>
      }
      footer={
        hasFooter ? (
          <VStack spacing={2}>
            <Show when={props.reason}>
              <HStack spacing={1} align="center">
                <Icon value="action/info_outline" size={3} color="text.warning" />
                <Text size="xSmall" color="warning">
                  {props.reason}
                </Text>
              </HStack>
            </Show>
            <Show when={isNotNilOrEmpty(props.actions)}>
              <HStack spacing={2} wrap>
                {props.actions?.map((action) => (
                  <Button
                    key={action.title}
                    title={action.title}
                    onClick={action.onClick}
                    variant={action.variant ?? 'outlined'}
                    isDisabled={action.isDisabled}
                    size="small"
                  />
                ))}
              </HStack>
            </Show>
          </VStack>
        ) : undefined
      }
    />
  );
}
